// ** React Import
import { useState } from 'react';

// ** Component Imports
import NavDrawer from './NavDrawer';
import VerticalNavHeader from './VerticalNavHeader';
import VerticalNavItems from './VerticalNavItems';
import ScrollBox from '@erikdakoda/ui/components/ScrollBox';

// ** Theme Config
import themeConfig from '../../../../config';
// ** Type Import
import type { LayoutProps } from '../../../../types';
// ** Theme Options
import Box from '@mui/material/Box';
// ** MUI Imports
import List from '@mui/material/List';

interface Props {
  navWidth: number;
  navVisible: boolean;
  collapsedNavWidth: number;
  hidden: LayoutProps['hidden'];
  navigationBorderWidth: number;
  toggleNavVisibility: () => void;
  settings: LayoutProps['settings'];
  children: LayoutProps['children'];
  setNavVisible: (value: boolean) => void;
  saveSettings: LayoutProps['saveSettings'];
  navMenuContent: LayoutProps['verticalLayoutProps']['navMenu']['content'];
  navMenuBranding: LayoutProps['verticalLayoutProps']['navMenu']['branding'];
  verticalNavItems: LayoutProps['verticalLayoutProps']['navMenu']['navItems'];
  navMenuProps: LayoutProps['verticalLayoutProps']['navMenu']['componentProps'];
  afterNavMenuContent: LayoutProps['verticalLayoutProps']['navMenu']['afterContent'];
  beforeNavMenuContent: LayoutProps['verticalLayoutProps']['navMenu']['beforeContent'];
}

const Navigation = (props: Props) => {
  // ** Props
  const { afterNavMenuContent, beforeNavMenuContent, navMenuContent: userNavMenuContent } = props;

  // ** States
  const [navHover, setNavHover] = useState<boolean>(false);
  const [groupActive, setGroupActive] = useState<string[]>([]);
  const [currentActiveGroup, setCurrentActiveGroup] = useState<string[]>([]);

  // ** Var
  const { afterVerticalNavMenuContentPosition, beforeVerticalNavMenuContentPosition } =
    themeConfig.config;

  const navMenuContentProps = {
    ...props,
    navHover,
    groupActive,
    setGroupActive,
    currentActiveGroup,
    setCurrentActiveGroup,
  };

  return (
    <NavDrawer {...props} navHover={navHover} setNavHover={setNavHover}>
      <Box
        sx={{
          height: '100%',
          display: 'grid',
          gridTemplateRows: 'min-content 1fr min-content',
          gridTemplateColumns: '100%',
        }}
      >
        <Box>
          <VerticalNavHeader {...props} navHover={navHover} />
        </Box>
        <Box>
          <ScrollBox>
            {beforeNavMenuContent && beforeVerticalNavMenuContentPosition === 'static'
              ? beforeNavMenuContent(navMenuContentProps)
              : null}
            {userNavMenuContent ? (
              userNavMenuContent(navMenuContentProps)
            ) : (
              <List
                className="nav-items"
                sx={{
                  pt: 0,
                  transition: 'padding .25s ease',
                  '& > :first-of-type': { mt: '0' },
                }}
              >
                <VerticalNavItems
                  navHover={navHover}
                  groupActive={groupActive}
                  setGroupActive={setGroupActive}
                  currentActiveGroup={currentActiveGroup}
                  setCurrentActiveGroup={setCurrentActiveGroup}
                  {...props}
                />
              </List>
            )}
            {afterNavMenuContent && afterVerticalNavMenuContentPosition === 'static'
              ? afterNavMenuContent(navMenuContentProps)
              : null}
          </ScrollBox>
        </Box>
        <Box>
          {afterNavMenuContent && afterVerticalNavMenuContentPosition === 'fixed'
            ? afterNavMenuContent(navMenuContentProps)
            : null}
        </Box>
      </Box>
    </NavDrawer>
  );
};

export default Navigation;
